import React, { useEffect, useMemo } from 'react';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import { Grid, Paper, Typography } from '@mui/material';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import { useDispatch, useSelector } from 'react-redux';
import { setIsWarehouseSelectedINFedX } from '@Reducers/Warehouse';
import SBCountryDropdown from '@Components/Dropdown/Country';
import SbTextField from '@Components/Textfield';
import DatePicker from '@Components/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
// import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import {
  PaymentTypeOptions,
  ProductTypeOptions,
  onForward,
  WhetherSupplyforExportIsOnPaymentOfIGSTOptions,
  WhetherAgainstBondOrUTOptions,
  WhetherMEISOptions,
  TermsOfTradeOptions,
} from './constants';
import SbButton from '@Components/Button';
import Divider from '@Components/Divider';
import * as Yup from 'yup';
import { DropdownOptionType } from '@Components/Dropdown/Type';

interface propsTypes {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

const aramexInformationSchema = Yup.object().shape({
  type: Yup.string().trim().required('Type is required'),
  accountNo: Yup.string().trim().required('Aramex Account is required'),
  countryOfManufacture: Yup.string().trim().required('Country of manufacture is required'),
  shipperTaxId: Yup.string().trim().required('Shipper Tax ID is required'),
  invoiceDate: Yup.date().required('Invoice Date is required').nullable(),
  productType: Yup.string().required('Product type is required'),
  paymentType: Yup.string().required('Payment type is required'),
  invoiceNumber: Yup.string().trim().required('Invoice number is required'),
  ShipmentType: Yup.string().required('Shipment type is required'),
  WhetherSupplyforExportIsOnPaymentOfIGST: Yup.string().when('ShipmentType', ([shipmentType], schema) => {
    return shipmentType === 'CSBV' ? schema.required('This field is required') : schema;
  }),
  WhetherAgainstBondOrUT: Yup.string().when('ShipmentType', ([shipmentType], schema) => {
    return shipmentType === 'CSBV' ? schema.required('This field is required') : schema;
  }),
  TermsOfTrade: Yup.string().when('ShipmentType', ([shipmentType], schema) => {
    return shipmentType === 'CSBV' ? schema.required('Terms of trade is required') : schema;
  }),
  WhetherMEIS: Yup.string().when('ShipmentType', ([shipmentType], schema) => {
    return shipmentType === 'CSBV' ? schema.required('This field is required') : schema;
  }),
  IGSTRate: Yup.number()
    .nullable()
    .when(['ShipmentType', 'WhetherSupplyforExportIsOnPaymentOfIGST'], ([shipmentType, igstValue], schema) => {
      return shipmentType === 'CSBV' && igstValue === 'YES'
        ? schema.required('IGST Rate is required when Supply for Export is on payment of IGST')
        : schema;
    }),
  IGSTAmount: Yup.number()
    .nullable()
    .when(['ShipmentType', 'WhetherSupplyforExportIsOnPaymentOfIGST'], ([shipmentType, igstValue], schema) => {
      return shipmentType === 'CSBV' && igstValue === 'YES'
        ? schema.required('IGST Amount is required when Supply for Export is on payment of IGST')
        : schema;
    }),
  ADCode: Yup.string(),
  FreightAmount: Yup.number().nullable(),
  InsuranceAmount: Yup.number().nullable(),
  BillToName: Yup.string(),
  BillToAddress: Yup.string(),
  BillToCountryCode: Yup.string(),
  BankAccountNumber: Yup.string()
    .trim()
    .default('')
    .when('ShipmentType', ([value], schema) => {
      return value === 'CSBV' ? schema.required('Bank Account Number is required for CSBV shipment') : schema;
    }),
  GSTN: Yup.string()
    .trim()
    .when('ShipmentType', ([value], schema) => {
      return value === 'CSBV' ? schema.required('GSTN is required for CSBV shipment') : schema;
    }),
  IEC: Yup.string()
    .trim()
    .when('ShipmentType', ([value], schema) => {
      return value === 'CSBV' ? schema.required('IEC is required for CSBV shipment') : schema;
    }),
});

export interface AramexInformation {
  type: string;
  accountNo: string;
  countryOfManufacture: string;
  shipperTaxId: string;
  invoiceDate: Dayjs | null;
  productType: string;
  paymentType: string;
  invoiceNumber: string;
  ShipmentType: string;
  WhetherSupplyforExportIsOnPaymentOfIGST: string;
  WhetherAgainstBondOrUT: string;
  TermsOfTrade: string;
  WhetherMEIS: string;
  IGSTRate: number | null;
  IGSTAmount: number | null;
  ADCode: string;
  FreightAmount: number | null;
  InsuranceAmount: number | null;
  BillToName: string;
  BillToAddress: string;
  BillToCountryCode: string;
  BankAccountNumber: string;
  GSTN: string;
  IEC: string;
}

const AramexInformation = (props: propsTypes) => {
  const { onClickForward, onClose, initialStates, isEditable, showFedExDetails, btnText } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { isWarehouseSelectedINFedX } = useSelector((state: RootStateType) => state.Warehouse);
  const { shipmentById, vendersAccount } = useSelector((state: RootStateType) => state.adminShipment);

  const formik = useFormik<AramexInformation>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      ...initialStates,
      invoiceNumber: shipmentById?._packageId.invoiceNumber || '',
      ShipmentType: initialStates?.ShipmentType || '',
      WhetherSupplyforExportIsOnPaymentOfIGST: initialStates?.WhetherSupplyforExportIsOnPaymentOfIGST || '',
      WhetherAgainstBondOrUT: initialStates?.WhetherAgainstBondOrUT || '',
      TermsOfTrade: initialStates?.TermsOfTrade || '',
      WhetherMEIS: initialStates?.WhetherMEIS || '',
      IGSTRate: initialStates?.IGSTRate || null,
      IGSTAmount: initialStates?.IGSTAmount || null,
      ADCode: initialStates?.ADCode || '',
      FreightAmount: initialStates?.FreightAmount || 0,
      InsuranceAmount: initialStates?.InsuranceAmount || 0,
      BillToName: initialStates?.BillToName || '',
      BillToAddress: initialStates?.BillToAddress || '',
      BillToCountryCode: initialStates?.BillToCountryCode || '',
      BankAccountNumber: initialStates?.BankAccountNumber || '',
      GSTN: initialStates?.GSTN || '',
      IEC: initialStates?.IEC || '',
    },
    validationSchema: aramexInformationSchema,
    onSubmit(values) {
      if (isWarehouseSelectedINFedX === '' || isWarehouseSelectedINFedX === 'false') {
        dispatch(setIsWarehouseSelectedINFedX('false'));
      } else {
        onForward({
          values,
          shipmentNumber: shipmentById?.shipmentNumber || '',
          dispatch,
          nextFunc: onClickForward,
        });
      }
    },
  });

  const { values, setFieldValue, errors, handleSubmit } = formik;

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })),
    [vendersAccount],
  );

  useEffect(() => {
    if (values.accountNo?.length && accountOptions?.length) {
      const matched = accountOptions.find(opt => opt.value.startsWith(values.accountNo));

      if (matched) {
        setFieldValue('accountNo', matched.value);
      }
    }
  }, [values.accountNo, accountOptions]);

  const isCSBV = values.ShipmentType === 'CSBV';
  const isIGSTRequired = isCSBV && values.WhetherSupplyforExportIsOnPaymentOfIGST === 'YES';

  const selectedAccount = useMemo<vendorAccountType | null>(() => {
    if (!values.accountNo || !vendersAccount?.length) return null;

    const [accountNo, id] = values.accountNo.split('-');

    return vendersAccount.find((acc: vendorAccountType) => acc.accountNo === accountNo && acc._id === id) || null;
  }, [values.accountNo, vendersAccount]);

  const shipmentTypeOptions = useMemo<DropdownOptionType[]>(() => {
    if (!selectedAccount?.CSBVAccount) {
      return [{ value: 'CSBIV', display: 'CSBIV' }];
    }

    return [
      { value: 'CSBIV', display: 'CSBIV' },
      { value: 'CSBV', display: 'CSBV' },
    ];
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount?.CSBVAccount && values.ShipmentType === 'CSBV') {
      setFieldValue('ShipmentType', 'CSBIV');
    }
  }, [selectedAccount, values.ShipmentType]);

  return (
    <div>
      {showFedExDetails && (
        <>
          <Paper
            variant="outlined"
            sx={{
              my: '2rem',
              p: '2rem',
            }}
          >
            <Grid container item xs={12}>
              <Grid container item mb={3}>
                <Grid item xs={12} md={6}>
                  <Typography className="font-medium l-text pb-3">Aramex Information</Typography>{' '}
                </Grid>
              </Grid>
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item columnSpacing={10} rowSpacing={2}>
                  <Grid item xs={12} md={6}>
                    <SBDropdown
                      disabled={!isEditable}
                      id="accountNo"
                      label={`Aramex Account`}
                      required
                      placeholder="Select"
                      name="accountNo"
                      value={[values.accountNo]}
                      options={accountOptions}
                      onChange={value => setFieldValue('accountNo', value[0])}
                      error={errors?.accountNo as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SBCountryDropdown
                      disabled={!isEditable}
                      required
                      label="Country of Manufacture"
                      value={values?.countryOfManufacture}
                      options={undefined}
                      onChange={value => {
                        setFieldValue('countryOfManufacture', value[0]);
                      }}
                      error={errors?.countryOfManufacture as string}
                    />
                  </Grid>
                </Grid>
                <Grid container item columnSpacing={10} rowSpacing={2}>
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      disabled={!isEditable}
                      label={`Invoice Number`}
                      required
                      value={values.invoiceNumber}
                      placeholder={`12345678`}
                      onChange={e => setFieldValue('invoiceNumber', e.target.value)}
                      name="invoiceNumber"
                      error={errors.invoiceNumber as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      disabled={!isEditable}
                      label={`Shipper Tax Id`}
                      required
                      placeholder=""
                      onChange={e => setFieldValue('shipperTaxId', e.target.value)}
                      name="shipperTaxId"
                      value={values.shipperTaxId}
                      error={errors.shipperTaxId as string}
                    />
                  </Grid>
                </Grid>
                <Grid container item columnSpacing={10} rowSpacing={2}>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      required
                      label="Invoice Date"
                      value={values?.invoiceDate || null}
                      handleChange={value => {
                        setFieldValue('invoiceDate', value);
                      }}
                      minDate={dayjs()}
                      disabled={!isEditable}
                      error={errors.invoiceDate as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SBDropdown
                      disabled={!isEditable}
                      id="productType"
                      label={`Product Type`}
                      required
                      placeholder="Select "
                      name="productType"
                      value={[values.productType]}
                      options={ProductTypeOptions}
                      onChange={value => {
                        setFieldValue('productType', value[0]);
                      }}
                      error={errors.productType as string}
                    />
                  </Grid>
                </Grid>
                <Grid container item columnSpacing={10} rowSpacing={2}>
                  <Grid item xs={12} md={6}>
                    <SBDropdown
                      disabled={!isEditable}
                      id="paymentType"
                      label={`Payment Type`}
                      required
                      placeholder="Select "
                      name="paymentType"
                      value={[values.paymentType]}
                      options={PaymentTypeOptions}
                      onChange={value => {
                        setFieldValue('paymentType', value[0]);
                      }}
                      error={errors.paymentType as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SBDropdown
                      disabled={!isEditable}
                      id="ShipmentType"
                      label={`Shipment Type`}
                      required
                      placeholder="Select "
                      name="ShipmentType"
                      value={[values.ShipmentType]}
                      options={shipmentTypeOptions}
                      onChange={value => {
                        const selected = value[0];
                        setFieldValue('ShipmentType', selected);

                        // Reset CSBV specific fields when ShipmentType changes from CSBV
                        if (selected !== 'CSBV') {
                          setFieldValue('WhetherSupplyforExportIsOnPaymentOfIGST', '');
                          setFieldValue('WhetherAgainstBondOrUT', '');
                          setFieldValue('TermsOfTrade', '');
                          setFieldValue('WhetherMEIS', '');
                          setFieldValue('IGSTRate', null);
                          setFieldValue('IGSTAmount', null);
                          setFieldValue('BankAccountNumber', '');
                          setFieldValue('GSTN', '');
                          setFieldValue('IEC', '');
                        }
                      }}
                      error={errors.ShipmentType as string}
                    />
                  </Grid>
                </Grid>

                {/* Conditional CSBV Fields - Only show when ShipmentType is CSBV */}
                {isCSBV && (
                  <>
                    {/* Whether Supply for Export Is On Payment Of IGST */}
                    <Grid container item columnSpacing={10} rowSpacing={2}>
                      <Grid item xs={12} md={6}>
                        <SBDropdown
                          disabled={!isEditable}
                          id="WhetherSupplyforExportIsOnPaymentOfIGST"
                          label={`Whether Supply for Export Is On Payment Of IGST`}
                          required={isCSBV}
                          placeholder="Select "
                          name="WhetherSupplyforExportIsOnPaymentOfIGST"
                          value={[values.WhetherSupplyforExportIsOnPaymentOfIGST]}
                          options={WhetherSupplyforExportIsOnPaymentOfIGSTOptions}
                          onChange={value => {
                            const selected = value[0];
                            setFieldValue('WhetherSupplyforExportIsOnPaymentOfIGST', selected);

                            // AUTO TOGGLE Bond/UT
                            if (selected === 'YES') {
                              setFieldValue('WhetherAgainstBondOrUT', 'NO');
                            } else if (selected === 'NO') {
                              setFieldValue('WhetherAgainstBondOrUT', 'YES');
                            }

                            if (selected === 'NO') {
                              setFieldValue('IGSTRate', 0);
                              setFieldValue('IGSTAmount', 0);
                            } else {
                              setFieldValue('IGSTRate', null);
                              setFieldValue('IGSTAmount', null);
                            }
                          }}
                          error={errors.WhetherSupplyforExportIsOnPaymentOfIGST as string}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SBDropdown
                          disabled={!isEditable}
                          id="WhetherAgainstBondOrUT"
                          label={`Whether Against Bond Or UT`}
                          required={isCSBV}
                          placeholder="Select "
                          name="WhetherAgainstBondOrUT"
                          value={[values.WhetherAgainstBondOrUT]}
                          options={WhetherAgainstBondOrUTOptions}
                          onChange={value => {
                            const selected = value[0];

                            setFieldValue('WhetherAgainstBondOrUT', selected);

                            // AUTO TOGGLE IGST
                            if (selected === 'YES') {
                              setFieldValue('WhetherSupplyforExportIsOnPaymentOfIGST', 'NO');
                              setFieldValue('IGSTRate', 0);
                              setFieldValue('IGSTAmount', 0);
                            } else if (selected === 'NO') {
                              setFieldValue('WhetherSupplyforExportIsOnPaymentOfIGST', 'YES');
                              setFieldValue('IGSTRate', null);
                              setFieldValue('IGSTAmount', null);
                            }
                          }}
                          error={errors.WhetherAgainstBondOrUT as string}
                        />
                      </Grid>
                    </Grid>

                    {/* Terms Of Trade */}
                    <Grid container item columnSpacing={10} rowSpacing={2}>
                      <Grid item xs={12} md={6}>
                        <SBDropdown
                          disabled={!isEditable}
                          id="TermsOfTrade"
                          label={`Terms Of Trade`}
                          required={isCSBV}
                          placeholder="Select "
                          name="TermsOfTrade"
                          value={[values.TermsOfTrade]}
                          options={TermsOfTradeOptions}
                          onChange={value => {
                            setFieldValue('TermsOfTrade', value[0]);
                          }}
                          error={errors.TermsOfTrade as string}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SBDropdown
                          disabled={!isEditable}
                          id="WhetherMEIS"
                          label={`Whether MEIS`}
                          required={isCSBV}
                          placeholder="Select "
                          name="WhetherMEIS"
                          value={[values.WhetherMEIS]}
                          options={WhetherMEISOptions}
                          onChange={value => {
                            setFieldValue('WhetherMEIS', value[0]);
                          }}
                          error={errors.WhetherMEIS as string}
                        />
                      </Grid>
                    </Grid>

                    <Grid container item columnSpacing={10} rowSpacing={2}>
                      <Grid item xs={12} md={6}>
                        <SbTextField
                          disabled={!isEditable}
                          label={`IGST Rate (%)`}
                          required={isIGSTRequired}
                          value={values.IGSTRate || ''}
                          placeholder="Enter IGST Rate"
                          onChange={e => setFieldValue('IGSTRate', e.target.value ? Number(e.target.value) : null)}
                          name="IGSTRate"
                          type="number"
                          error={errors.IGSTRate as string}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SbTextField
                          disabled={!isEditable}
                          label={`IGST Amount`}
                          required={isIGSTRequired}
                          placeholder="Enter IGST Amount"
                          onChange={e => setFieldValue('IGSTAmount', e.target.value ? Number(e.target.value) : null)}
                          name="IGSTAmount"
                          value={values.IGSTAmount || ''}
                          type="number"
                          error={errors.IGSTAmount as string}
                        />
                      </Grid>
                    </Grid>

                    {/* CSBV Specific Fields */}
                    <Grid container item columnSpacing={10} rowSpacing={2}>
                      <Grid item xs={12} md={6}>
                        <SbTextField
                          disabled={!isEditable}
                          label={`Bank Account Number`}
                          required={isCSBV}
                          placeholder="Enter Bank Account Number"
                          onChange={e => setFieldValue('BankAccountNumber', e.target.value)}
                          name="BankAccountNumber"
                          value={values.BankAccountNumber}
                          error={errors.BankAccountNumber as string}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SbTextField
                          disabled={!isEditable}
                          label={`GSTN`}
                          required={isCSBV}
                          placeholder="Enter GSTN"
                          onChange={e => setFieldValue('GSTN', e.target.value)}
                          name="GSTN"
                          value={values.GSTN}
                          error={errors.GSTN as string}
                        />
                      </Grid>
                    </Grid>
                    <Grid container item columnSpacing={10} rowSpacing={2}>
                      <Grid item xs={12} md={6}>
                        <SbTextField
                          disabled={!isEditable}
                          label={`IEC`}
                          required={isCSBV}
                          placeholder="Enter IEC"
                          onChange={e => setFieldValue('IEC', e.target.value)}
                          name="IEC"
                          value={values.IEC}
                          error={errors.IEC as string}
                        />
                      </Grid>
                    </Grid>
                    {/* Non-CSBV Specific Fields (always visible) */}
                    <Grid container item columnSpacing={10} rowSpacing={2}>
                      <Grid item xs={12} md={6}>
                        <SbTextField
                          disabled={!isEditable}
                          label={`AD Code`}
                          placeholder="Enter AD Code"
                          onChange={e => setFieldValue('ADCode', e.target.value)}
                          name="ADCode"
                          value={values.ADCode}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SbTextField
                          disabled={!isEditable}
                          label={`Freight Amount`}
                          placeholder="Enter Freight Amount"
                          onChange={e => setFieldValue('FreightAmount', e.target.value ? Number(e.target.value) : null)}
                          name="FreightAmount"
                          value={values.FreightAmount || ''}
                          type="number"
                        />
                      </Grid>
                    </Grid>
                    <Grid container item columnSpacing={10} rowSpacing={2}>
                      <Grid item xs={12} md={6}>
                        <SbTextField
                          disabled={!isEditable}
                          label={`Insurance Amount`}
                          placeholder="Enter Insurance Amount"
                          onChange={e =>
                            setFieldValue('InsuranceAmount', e.target.value ? Number(e.target.value) : null)
                          }
                          name="InsuranceAmount"
                          value={values.InsuranceAmount || ''}
                          type="number"
                        />
                      </Grid>
                    </Grid>

                    {/* Bill To Information */}
                    <Grid container item columnSpacing={10} rowSpacing={2}>
                      <Grid item xs={12} md={6}>
                        <SbTextField
                          disabled={!isEditable}
                          label={`Bill To Name`}
                          placeholder="Enter Bill To Name"
                          onChange={e => setFieldValue('BillToName', e.target.value)}
                          name="BillToName"
                          value={values.BillToName}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SbTextField
                          disabled={!isEditable}
                          label={`Bill To Country Code`}
                          placeholder="Enter Bill To Country Code"
                          onChange={e => setFieldValue('BillToCountryCode', e.target.value)}
                          name="BillToCountryCode"
                          value={values.BillToCountryCode}
                        />
                      </Grid>
                    </Grid>
                    <Grid container item columnSpacing={10} rowSpacing={2}>
                      <Grid item xs={12}>
                        <SbTextField
                          disabled={!isEditable}
                          label={`Bill To Address`}
                          placeholder="Enter Bill To Address"
                          onChange={e => setFieldValue('BillToAddress', e.target.value)}
                          name="BillToAddress"
                          value={values.BillToAddress}
                          multiline
                          rows={3}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                <Divider className="mt-3 mb-3" />
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      disabled={!isEditable}
                      variant="contained"
                      sx={{ borderRadius: `4px !important` }}
                      className="w-full"
                      onClick={_event => {
                        handleSubmit();
                      }}
                    >
                      {btnText}
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        sx={{ borderRadius: `4px !important` }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
      {/* {aramexDetails?.response && (
        <Grid container item>
          <Grid
            item
            xs={'auto'}
            border={'1px solid'}
            className={`border-grey font-medium md-text p-2`}
            borderRadius={1}
          >
            This shipment has been forwarded.
            <Box
              component={'span'}
              className="font-medium md-text ml-1"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById?.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )} */}
    </div>
  );
};

export default AramexInformation;
